import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Snackbar,
  Alert,
  IconButton,
  Drawer,
  Box,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import MenuIcon from '@mui/icons-material/Menu';
import InfoIcon from '@mui/icons-material/Info'; // or any icon for the right sidebar

import TickerSidebar from './TickerSidebar';
import ReportsSidebar from './ReportSidebar';
import ReportArea from './ReportArea';
import api from '../services/api'; // Import the Axios instance

interface Report {
  id: string;
  title: string;
  created: string;
}

interface ReportData {
  id: string;
  title: string;
  content: string;
  raw_data: string;
  rating: number;
}

const Reports: React.FC = () => {
  const [reports, setReports] = useState<Report[]>([]);
  const [selectedReportId, setSelectedReportId] = useState<string | null>(null);
  const [selectedReport, setSelectedReport] = useState<ReportData | null>(null);
  const [isFetchingReports, setIsFetchingReports] = useState(false);
  const [isFetchingReportContent, setIsFetchingReportContent] = useState(false);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // For responsive layout
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Drawer states
  const [leftOpen, setLeftOpen] = useState(false);
  const [rightOpen, setRightOpen] = useState(false);

  useEffect(() => {
    fetchReports();
  }, []);

  useEffect(() => {
    if (selectedReportId) {
      fetchReportContent(selectedReportId);
    }
  }, [selectedReportId]);

  // Function to fetch all reports
  const fetchReports = async () => {
    setIsFetchingReports(true);
    try {
      const response = await api.get<Report[]>('/report/all');
      setReports(response.data);
    } catch (error: any) {
      console.error('Error fetching reports:', error);
      setError(
          error.response?.data?.message ||
          'Failed to fetch reports. Please try again.'
      );
    } finally {
      setIsFetchingReports(false);
    }
  };

  // Function to fetch content of a specific report
  const fetchReportContent = async (reportId: string) => {
    setIsFetchingReportContent(true);
    try {
      const response = await api.get<ReportData>('/report/', {
        params: { report_id: reportId },
      });
      setSelectedReport(response.data);
    } catch (error: any) {
      console.error('Error fetching report content:', error);
      setError(
          error.response?.data?.message ||
          'Failed to fetch report content. Please try again.'
      );
    } finally {
      setIsFetchingReportContent(false);
    }
  };

  // Handler for selecting a report
  const handleSelectReport = (reportId: string) => {
    if (isGeneratingReport || isFetchingReportContent) return;
    setSelectedReportId(reportId);
  };

  // Handler for generating a new report
  const handleGenerateReport = async (tickerId: string) => {
    setIsGeneratingReport(true);
    try {
      const response = await api.post<ReportData>('/agent/report', {
        ticker_id: tickerId,
      });

      const newReport = response.data;

      // Update the reports list with the new report
      setReports((prevReports) => [
        ...prevReports,
        {
          id: newReport.id,
          title: newReport.title,
          created: new Date().toISOString(),
        },
      ]);

      // Select the newly generated report
      setSelectedReportId(newReport.id);
      setSelectedReport(newReport);
    } catch (error: any) {
      console.error('Error generating report:', error);
      setError(
          error.response?.data?.message ||
          'Failed to generate report. Please try again.'
      );
    } finally {
      setIsGeneratingReport(false);
    }
  };

  return (
      <Container
          maxWidth="xl"
          sx={{ mt: 4, height: '85vh', display: 'flex', flexDirection: 'column' }}
      >
        {/*
        Optionally, show two icon buttons for toggling sidebars on mobile.
        You can also place these inside a small toolbar, or at the top of <ReportArea>, etc.
      */}
        {isMobile && (
            <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  mb: 1,
                }}
            >
              {/* Left sidebar toggle (ReportsSidebar) */}
              <IconButton color="primary" onClick={() => setLeftOpen(true)}>
                <MenuIcon />
              </IconButton>

              {/* Right sidebar toggle (TickerSidebar) */}
              <IconButton color="primary" onClick={() => setRightOpen(true)}>
                <InfoIcon />
              </IconButton>
            </Box>
        )}

        <Grid container spacing={2} sx={{ flexGrow: 1, height: '100%' }}>
          {/* Left Sidebar - hidden on mobile */}
          {!isMobile && (
              <Grid item xs={12} md={3}>
                <ReportsSidebar
                    reports={reports}
                    isFetchingReports={isFetchingReports}
                    selectedReportId={selectedReportId}
                    handleSelectReport={handleSelectReport}
                    isGeneratingReport={isGeneratingReport}
                    isFetchingReportContent={isFetchingReportContent}
                />
              </Grid>
          )}

          {/* Main Content (Report Area) */}
          <Grid
              item
              xs={12}
              md={6}
              sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}
          >
            <ReportArea
                selectedReport={selectedReport}
                isFetchingReportContent={isFetchingReportContent}
                handleGenerateReport={handleGenerateReport}
                isGeneratingReport={isGeneratingReport}
            />
          </Grid>

          {/* Right Sidebar (Ticker Sidebar) - hidden on mobile */}
          {!isMobile && (
              <Grid item xs={12} md={3}>
                <TickerSidebar />
              </Grid>
          )}
        </Grid>

        {/* Drawer for the Left Sidebar (ReportsSidebar) */}
        <Drawer anchor="left" open={leftOpen} onClose={() => setLeftOpen(false)}>
          <Box sx={{ width: 250, p: 2 }}>
            <ReportsSidebar
                reports={reports}
                isFetchingReports={isFetchingReports}
                selectedReportId={selectedReportId}
                handleSelectReport={handleSelectReport}
                isGeneratingReport={isGeneratingReport}
                isFetchingReportContent={isFetchingReportContent}
            />
          </Box>
        </Drawer>

        {/* Drawer for the Right Sidebar (TickerSidebar) */}
        <Drawer anchor="right" open={rightOpen} onClose={() => setRightOpen(false)}>
          <Box sx={{ width: 250, p: 2 }}>
            <TickerSidebar />
          </Box>
        </Drawer>

        {/* Snackbar for Error Messages */}
        <Snackbar
            open={!!error}
            autoHideDuration={6000}
            onClose={() => setError(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
              onClose={() => setError(null)}
              severity="error"
              sx={{ width: '100%' }}
          >
            {error}
          </Alert>
        </Snackbar>
      </Container>
  );
};

export default Reports;
